/**
 * Swappable source for "the token's current pump.fun market cap, in SOL".
 *
 * Player-facing UI (TokenMcapPanel) and the milestone-progress bar both read
 * through `mcapSource` rather than touching a concrete implementation. When
 * the real pump.fun feed arrives, swap the singleton at the bottom of this
 * file — nothing else changes.
 *
 * Contract:
 *   - `sampleNow()` is cheap and synchronous; safe to call on every render
 *     or every animation frame.
 *   - `subscribe(cb)` calls `cb` on every tick the source produces. Returns
 *     an unsubscribe fn. The first call may fire synchronously with the
 *     current value to seed UI.
 *   - All values are in **lamports**, matching what `Season.peak_mcap_lamports`
 *     and `Season.next_milestone_mcap_lamports` store on-chain. Display code
 *     converts via `fmtSol`.
 */
import { LAMPORTS_PER_SOL } from "@solana/web3.js";

export interface McapSample {
  /** Posted/observed market cap in lamports. */
  lamports: number;
  /** Wall-clock timestamp of the sample. */
  ts: number;
  /**
   * Where the value came from. UI may badge "stub" so it's obvious this is
   * not the real feed yet.
   */
  source: "stub" | "pumpfun" | "manual";
}

export type McapListener = (s: McapSample) => void;

export interface McapSource {
  sampleNow(): McapSample;
  subscribe(cb: McapListener): () => void;
  /**
   * Dev hook: override the next sample. Only used by DevConsole "fire a
   * milestone" buttons. The real implementation should reject manual
   * overrides (no-op) so production UI can't be spoofed.
   */
  setManual(lamports: number): void;
}

/**
 * Drifts upward with a small sine wiggle so the player sees the bar
 * actually move. Starts at 60 SOL mcap, doubles roughly every 90s — close
 * enough to feel like a launch curve without becoming a stress test.
 */
class StubMcapSource implements McapSource {
  private readonly startTs = Date.now();
  private readonly listeners = new Set<McapListener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private manualOverride: number | null = null;

  private intrinsic(): number {
    const t = (Date.now() - this.startTs) / 1000;
    const growthSol = 60 * Math.pow(1.5, t / 90);
    const wiggle = 1 + 0.04 * Math.sin(t / 7);
    return Math.floor(growthSol * wiggle * LAMPORTS_PER_SOL);
  }

  sampleNow(): McapSample {
    if (this.manualOverride !== null) {
      return { lamports: this.manualOverride, ts: Date.now(), source: "manual" };
    }
    return { lamports: this.intrinsic(), ts: Date.now(), source: "stub" };
  }

  subscribe(cb: McapListener): () => void {
    this.listeners.add(cb);
    cb(this.sampleNow());
    if (!this.timer) {
      this.timer = setInterval(() => {
        const s = this.sampleNow();
        for (const fn of this.listeners) fn(s);
      }, 1000);
    }
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  setManual(lamports: number): void {
    this.manualOverride = lamports;
    const s: McapSample = { lamports, ts: Date.now(), source: "manual" };
    for (const fn of this.listeners) fn(s);
  }
}

/**
 * Real source: polls the API's `/api/koth/token-market` (one server-side poller
 * behind it reads DexScreener + converts USD mcap → lamports). Cheap + scalable;
 * every browser reads the same edge-cacheable endpoint.
 */
class ApiMcapSource implements McapSource {
  private readonly listeners = new Set<McapListener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private last: McapSample = { lamports: 0, ts: 0, source: "pumpfun" };

  private base(): string {
    const env = (import.meta as { env?: { VITE_API_URL?: string } }).env;
    return env?.VITE_API_URL ?? "http://localhost:3030";
  }

  private async poll(): Promise<void> {
    try {
      const r = await fetch(`${this.base()}/api/koth/token-market`, {
        cache: "no-store",
      });
      if (!r.ok) return;
      const j = (await r.json()) as { marketCapLamports?: number | null };
      const lam = j?.marketCapLamports;
      if (typeof lam === "number" && lam >= 0) {
        this.last = { lamports: lam, ts: Date.now(), source: "pumpfun" };
        for (const fn of this.listeners) fn(this.last);
      }
    } catch {
      // network blip — keep last sample
    }
  }

  sampleNow(): McapSample {
    return this.last;
  }

  subscribe(cb: McapListener): () => void {
    this.listeners.add(cb);
    cb(this.last);
    if (!this.timer) {
      void this.poll();
      this.timer = setInterval(() => void this.poll(), 15000);
    }
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  // Real feed ignores manual overrides so production UI can't be spoofed.
  setManual(): void {
    /* no-op */
  }
}

/**
 * Production reads the real token feed through the API (VITE_API_URL set);
 * local dev with no API falls back to the visual stub.
 */
function apiConfigured(): boolean {
  const env = (import.meta as { env?: { VITE_API_URL?: string } }).env;
  return !!(env?.VITE_API_URL && env.VITE_API_URL.length > 0);
}

export const mcapSource: McapSource = apiConfigured()
  ? new ApiMcapSource()
  : new StubMcapSource();
